import { STAGE_INSTRUCTIONS, TOOL_NAMES, isToolIndex, type ToolIndex } from '../config/constants';
import { AudioFeedback } from '../audio/AudioFeedback';
import { createPhaserGame, type PhaserHandle } from '../game/PhaserGame';
import type { CleaningResult } from '../game/types';
import { CleaningModel } from '../systems/CleaningModel';
import { clearSnapshot, loadSnapshot, saveSnapshot } from '../utils/storage';

function requiredElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`缺少必要界面元素：#${id}`);
  }
  return element as T;
}

export class AppController {
  private readonly model = new CleaningModel();
  private readonly audio = new AudioFeedback();
  private readonly startScreen = requiredElement<HTMLElement>('start-screen');
  private readonly gameScreen = requiredElement<HTMLElement>('game-screen');
  private readonly completeScreen = requiredElement<HTMLElement>('complete-screen');
  private readonly startButton = requiredElement<HTMLButtonElement>('start-button');
  private readonly pauseButton = requiredElement<HTMLButtonElement>('pause-button');
  private readonly continueButton = requiredElement<HTMLButtonElement>('continue-button');
  private readonly pauseOverlay = requiredElement<HTMLElement>('pause-overlay');
  private readonly progressValue = requiredElement<HTMLElement>('progress-value');
  private readonly progressFill = requiredElement<HTMLElement>('progress-fill');
  private readonly progressTrack = document.querySelector<HTMLElement>('[role="progressbar"]');
  private readonly instruction = requiredElement<HTMLElement>('instruction');
  private readonly completionTime = requiredElement<HTMLOutputElement>('completion-time');
  private readonly toast = requiredElement<HTMLElement>('toast');
  private readonly toolButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.tool-button'));
  private readonly orientation = window.matchMedia('(orientation: landscape)');
  private phaser: PhaserHandle | null = null;
  private toastTimer = 0;

  initialize(): void {
    const saved = loadSnapshot();
    if (saved && !this.model.restore(saved)) {
      clearSnapshot();
    }

    this.bindControls();
    this.phaser = createPhaserGame(this.model, {
      onReady: () => {
        this.startButton.disabled = false;
        this.render();
      },
      onCleaning: (result) => this.handleCleaning(result),
      onUnavailable: (reason) => this.handleUnavailable(reason),
    });
    this.bindLifecycle();
    this.render();
  }

  private bindControls(): void {
    this.startButton.addEventListener('click', () => {
      void this.audio.unlock();
      this.model.start();
      this.phaser?.scene.resetVisuals();
      this.persistAndRender();
    });
    this.pauseButton.addEventListener('click', () => this.togglePause());
    this.continueButton.addEventListener('click', () => this.togglePause());
    requiredElement<HTMLButtonElement>('restart-button').addEventListener('click', () => {
      this.model.restart();
      this.phaser?.scene.resetVisuals();
      this.showToast('已重新開始');
      this.persistAndRender();
    });
    requiredElement<HTMLButtonElement>('replay-button').addEventListener('click', () => {
      void this.audio.unlock();
      this.model.restart();
      this.phaser?.scene.resetVisuals();
      this.persistAndRender();
    });
    requiredElement<HTMLButtonElement>('home-button').addEventListener('click', () => {
      this.model.returnToStart();
      this.phaser?.scene.resetVisuals();
      clearSnapshot();
      this.render();
    });
    for (const button of this.toolButtons) {
      button.addEventListener('click', () => {
        const tool = Number(button.dataset.tool);
        if (!isToolIndex(tool)) {
          return;
        }
        const correct = this.model.selectTool(tool);
        if (this.model.state === 'running' && !correct) {
          this.showToast(`目前需要：${TOOL_NAMES[this.model.expectedStage] ?? TOOL_NAMES[0]}`);
        }
        this.persistAndRender();
      });
    }
  }

  private bindLifecycle(): void {
    const pauseForInterruption = () => {
      if (this.model.pause()) {
        this.persistAndRender();
      }
    };
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        pauseForInterruption();
      }
    });
    window.addEventListener('blur', pauseForInterruption);
    this.orientation.addEventListener('change', (event) => {
      if (event.matches) {
        pauseForInterruption();
      }
    });
    window.addEventListener('beforeunload', () => saveSnapshot(this.model.snapshot()));
  }

  private togglePause(): void {
    if (this.model.state === 'running') {
      this.model.pause();
    } else if (this.model.state === 'paused') {
      this.model.resume();
    }
    this.persistAndRender();
  }

  private handleCleaning(result: CleaningResult): void {
    this.audio.playStroke(result.stage);
    if (result.stageCompleted) {
      this.audio.playMilestone();
      this.audio.vibrate();
      if (!result.gameCompleted) {
        this.showToast(`下一步：${TOOL_NAMES[this.model.expectedStage] ?? ''}`);
      }
    }
    this.persistAndRender();
  }

  private handleUnavailable(reason: 'paused' | 'wrong-tool'): void {
    this.showToast(reason === 'paused'
      ? '遊戲已暫停，點擊「繼續」再清潔'
      : `目前需要：${TOOL_NAMES[this.model.expectedStage] ?? TOOL_NAMES[0]}`);
  }

  private persistAndRender(): void {
    saveSnapshot(this.model.snapshot());
    this.render();
  }

  private render(): void {
    this.startScreen.hidden = this.model.state !== 'not-started';
    this.gameScreen.hidden = this.model.state === 'not-started' || this.model.state === 'completed';
    this.completeScreen.hidden = this.model.state !== 'completed';
    this.pauseOverlay.hidden = this.model.state !== 'paused';

    const percentage = Math.round(this.model.totalProgress * 100);
    this.progressValue.textContent = `${percentage}%`;
    this.progressFill.style.width = `${percentage}%`;
    this.progressTrack?.setAttribute('aria-valuenow', String(percentage));

    const stage = Math.min(this.model.expectedStage, STAGE_INSTRUCTIONS.length - 1) as ToolIndex;
    if (this.model.state === 'paused') {
      this.instruction.textContent = '已暫停，休息一下也沒關係';
    } else {
      this.instruction.textContent = STAGE_INSTRUCTIONS[stage];
    }

    const pauseLabel = this.model.state === 'paused' ? '繼續' : '暫停';
    this.pauseButton.querySelector('b')?.replaceChildren(pauseLabel);
    this.pauseButton.setAttribute('aria-label', pauseLabel);

    for (const button of this.toolButtons) {
      const tool = Number(button.dataset.tool);
      const selected = tool === this.model.selectedTool;
      const expected = tool === this.model.expectedStage && this.model.state === 'running';
      button.classList.toggle('selected', selected);
      button.classList.toggle('expected', expected && !selected);
      button.setAttribute('aria-pressed', String(selected));
    }

    if (this.model.state === 'completed') {
      this.completionTime.textContent = `本次清潔 ${Math.max(1, Math.round(this.model.elapsedMs / 1000))} 秒`;
    }
  }

  private showToast(message: string): void {
    window.clearTimeout(this.toastTimer);
    this.toast.textContent = message;
    this.toast.hidden = false;
    this.toastTimer = window.setTimeout(() => {
      this.toast.hidden = true;
    }, 1_800);
  }
}
